import React, { useEffect, useState, useRef } from 'react';
import { Snackbar, Box,
  Card,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  IconButton,
  Avatar,
  Chip, } from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Image as ImageIcon,
} from '@mui/icons-material';
import { Meal, mealsAPI, getImageUrl } from '../../services/api';
import { formatPersianNumber, toPersianNumber, toEnglishNumber } from '../../utils/persianNumber';

const ManagerMeals: React.FC = () => {
  const [meals, setMeals] = useState<Meal[]>([]);
  const [loading, setLoading] = useState(true);
  const [openModal, setOpenModal] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [editingMeal, setEditingMeal] = useState<Meal | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    price: '',
    staffShare: '',
    imageUrl: '',
    ingredients: '',
    calories: '',
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    loadMeals();
  }, []);

  const loadMeals = async () => {
    try {
      setLoading(true);
      const data = await mealsAPI.getMeals();
      setMeals(data);
    } catch (err: any) {
      setError('خطا در بارگذاری غذاها');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (meal: Meal) => {
    setEditingMeal(meal);
    setFormData({
      title: meal.title,
      description: meal.description || '',
      price: meal.price?.toString() || '',
      staffShare: meal.staffShare?.toString() || '',
      imageUrl: meal.imageUrl || '',
      ingredients: meal.ingredients?.join(', ') || '',
      calories: meal.calories?.toString() || '',
    });
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setEditingMeal(null);
    setFormData({
      title: '',
      description: '',
      price: '',
      staffShare: '',
      imageUrl: '',
      ingredients: '',
      calories: '',
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      const ingredientsArray = formData.ingredients
        ? formData.ingredients.split(',').map(ing => ing.trim()).filter(ing => ing.length > 0)
        : undefined;

      if (editingMeal) {
        await mealsAPI.updateMeal(editingMeal.id, {
          title: formData.title,
          description: formData.description || undefined,
          price: parseFloat(formData.price),
          staffShare: parseFloat(formData.staffShare),
          imageUrl: formData.imageUrl || undefined,
          ingredients: ingredientsArray,
          calories: formData.calories ? parseInt(formData.calories) : 0,
        });
        setSuccess('آیتم با موفقیت به‌روزرسانی شد');
      } else {
        await mealsAPI.createMeal({
          title: formData.title,
          description: formData.description || undefined,
          price: parseFloat(formData.price),
          staffShare: parseFloat(formData.staffShare),
          imageUrl: formData.imageUrl || undefined,
          ingredients: ingredientsArray,
          calories: formData.calories ? parseInt(formData.calories) : 0,
        });
        setSuccess('آیتم با موفقیت اضافه شد');
      }

      handleCloseModal();
      loadMeals();
    } catch (err: any) {
      setError(editingMeal ? 'خطا در به‌روزرسانی آیتم' : 'خطا در اضافه کردن آیتم');
    }
  };

  const handleImageClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      try {
        setUploadingImage(true);
        setError('');
        const mealId = editingMeal?.id || undefined;
        const imageUrl = await mealsAPI.uploadImage(file, mealId);
        setFormData({ ...formData, imageUrl });
        setSuccess('عکس با موفقیت آپلود شد');
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      } catch (err: any) {
        setError('خطا در آپلود عکس');
      } finally {
        setUploadingImage(false);
      }
    }
  };

  const handleDelete = async (mealId: string) => {
    if (!window.confirm('آیا از حذف این آیتم مطمئن هستید؟')) return;

    try {
      await mealsAPI.deleteMeal(mealId);
      setSuccess('آیتم با موفقیت حذف شد');
      loadMeals();
    } catch (err: any) {
      setError('خطا در حذف آیتم');
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 4,
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontFamily: 'Vazir, sans-serif',
            fontWeight: 600,
          }}
        >
          مدیریت آیتم‌های غذایی
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpenModal(true)}
          sx={{ fontFamily: 'Vazir, sans-serif' }}
        >
          افزودن آیتم
        </Button>
      </Box>

      <Snackbar open={!!error} autoHideDuration={2000} onClose={() => { if(typeof setError === 'function') setError(''); }} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
  <Alert severity="error" sx={{ width: '100%', fontFamily: 'Vazir, sans-serif' }} onClose={() => { if(typeof setError === 'function') setError(''); }}>
    {error}
  </Alert>
</Snackbar>
      <Snackbar open={!!success} autoHideDuration={2000} onClose={() => setSuccess('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
  <Alert severity="success" sx={{ width: '100%', fontFamily: 'Vazir, sans-serif' }} onClose={() => setSuccess('')}>
    {success}
  </Alert>
</Snackbar>

      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 600 }}>
                  عکس
                </TableCell>
                <TableCell sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 600 }}>
                  عنوان
                </TableCell>
                <TableCell sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 600 }}>
                  قیمت
                </TableCell>
                <TableCell sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 600 }}>
                  سهم پرسنل
                </TableCell>
                <TableCell sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 600 }}>
                  کالری
                </TableCell>
                <TableCell sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 600 }}>
                  مواد تشکیل دهنده
                </TableCell>
                <TableCell sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 600 }}>
                  عملیات
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {meals.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontFamily: 'Vazir, sans-serif' }}
                    >
                      هیچ آیتمی تعریف نشده است
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                meals.map((meal) => (
                  <TableRow key={meal.id} hover>
                    <TableCell>
                      {meal.imageUrl ? (
                        <Box
                          component="img"
                          src={getImageUrl(meal.imageUrl)}
                          alt={meal.title}
                          sx={{
                            width: 60,
                            height: 60,
                            objectFit: 'contain',
                            borderRadius: 1,
                            bgcolor: 'action.disabledBackground',
                          }}
                        />
                      ) : (
                        <Avatar
                          variant="rounded"
                          sx={{
                            width: 60,
                            height: 60,
                            bgcolor: 'action.disabledBackground',
                          }}
                        >
                          <ImageIcon />
                        </Avatar>
                      )}
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'Vazir, sans-serif' }}>
                      {meal.title}
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'Vazir, sans-serif' }}>
                      {formatPersianNumber(meal.price)} تومان
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'Vazir, sans-serif' }}>
                      {formatPersianNumber(meal.staffShare)} تومان
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'Vazir, sans-serif' }}>
                      {meal.calories || '-'}
                    </TableCell>
                    <TableCell>
                      {meal.ingredients && meal.ingredients.length > 0 ? (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {meal.ingredients.slice(0, 3).map((ing, idx) => (
                            <Chip
                              key={idx}
                              label={ing}
                              size="small"
                              sx={{ fontFamily: 'Vazir, sans-serif', fontSize: '0.75rem' }}
                            />
                          ))}
                          {meal.ingredients.length > 3 && (
                            <Chip
                              label={`+${meal.ingredients.length - 3}`}
                              size="small"
                              sx={{ fontFamily: 'Vazir, sans-serif', fontSize: '0.75rem' }}
                            />
                          )}
                        </Box>
                      ) : (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ fontFamily: 'Vazir, sans-serif' }}
                        >
                          -
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <IconButton
                        color="primary"
                        size="small"
                        onClick={() => handleEdit(meal)}
                        sx={{ mr: 1 }}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        color="error"
                        size="small"
                        onClick={() => handleDelete(meal.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      <Dialog
        open={openModal}
        onClose={handleCloseModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
          },
        }}
      >
        <DialogTitle sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 600, textAlign: 'center' }}>
          {editingMeal ? 'ویرایش آیتم' : 'افزودن آیتم'}
        </DialogTitle>
        <Box component="form" onSubmit={handleSubmit}>
          <DialogContent sx={{ px: 3, py: 2 }}>
            {/* Hidden file input */}
            <input
              type="file"
              accept="image/*"
              ref={fileInputRef}
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />

            {/* Image Upload - Circular at top */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
              {formData.imageUrl ? (
                <Box sx={{ textAlign: 'center' }}>
                  <Avatar
                    src={getImageUrl(formData.imageUrl)}
                    alt="Preview"
                    sx={{
                      width: 120,
                      height: 120,
                      cursor: 'pointer',
                      border: '3px solid',
                      borderColor: 'primary.main',
                      mb: 1,
                      '&:hover': {
                        opacity: 0.8,
                      },
                    }}
                    onClick={handleImageClick}
                  />
                  <Button
                    variant="text"
                    color="error"
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setFormData({ ...formData, imageUrl: '' });
                    }}
                    sx={{ fontFamily: 'Vazir, sans-serif', fontSize: '0.75rem' }}
                  >
                    حذف عکس
                  </Button>
                </Box>
              ) : (
                <Avatar
                  sx={{
                    width: 120,
                    height: 120,
                    bgcolor: 'action.disabledBackground',
                    cursor: 'pointer',
                    border: '2px dashed',
                    borderColor: 'divider',
                    '&:hover': {
                      bgcolor: 'action.hover',
                      borderColor: 'primary.main',
                    },
                  }}
                  onClick={handleImageClick}
                >
                  <ImageIcon sx={{ fontSize: 40, color: 'text.secondary' }} />
                </Avatar>
              )}
            </Box>

            {/* Form Fields in Grid */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(2, 1fr)',
                },
                gap: 2,
              }}
            >
              <TextField
                fullWidth
                label="نام آیتم"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
                placeholder="مثال: کباب کوبیده"
                InputLabelProps={{
                  sx: { fontFamily: 'Vazir, sans-serif' },
                }}
                inputProps={{
                  sx: { fontFamily: 'Vazir, sans-serif' },
                }}
              />
              <TextField
                fullWidth
                label="قیمت (تومان)"
                value={formData.price}
                onChange={(e) => setFormData({ ...formData, price: toEnglishNumber(e.target.value) })}
                required
                placeholder="مثال: 50000"
                InputLabelProps={{
                  sx: { fontFamily: 'Vazir, sans-serif' },
                }}
                inputProps={{
                  sx: { fontFamily: 'Vazir, sans-serif' },
                  min: 0,
                  inputMode: 'numeric'
                }}
              />
              <TextField
                fullWidth
                label="قیمت سهم پرسنل (تومان)"
                value={formData.staffShare}
                onChange={(e) => setFormData({ ...formData, staffShare: toEnglishNumber(e.target.value) })}
                required
                placeholder="مثال: 20000"
                InputLabelProps={{
                  sx: { fontFamily: 'Vazir, sans-serif' },
                }}
                inputProps={{
                  sx: { fontFamily: 'Vazir, sans-serif' },
                  min: 0,
                  inputMode: 'numeric'
                }}
              />
              <TextField
                fullWidth
                label="کالری"
                value={formData.calories}
                onChange={(e) => setFormData({ ...formData, calories: toEnglishNumber(e.target.value) })}
                placeholder="مثال: 500"
                InputLabelProps={{
                  sx: { fontFamily: 'Vazir, sans-serif' },
                }}
                inputProps={{
                  sx: { fontFamily: 'Vazir, sans-serif' },
                  min: 0,
                  inputMode: 'numeric'
                }}
              />
              <TextField
                fullWidth
                label="مواد تشکیل دهنده"
                placeholder="برنج، مرغ، سبزیجات"
                value={formData.ingredients}
                onChange={(e) => setFormData({ ...formData, ingredients: e.target.value })}
                InputLabelProps={{
                  sx: { fontFamily: 'Vazir, sans-serif' },
                }}
                inputProps={{
                  sx: { fontFamily: 'Vazir, sans-serif' },
                }}
                helperText="با کاما جدا کنید"
                FormHelperTextProps={{
                  sx: { fontFamily: 'Vazir, sans-serif', fontSize: '0.7rem' },
                }}
              />
            </Box>
            <TextField
              fullWidth
              label="توضیحات"
              multiline
              rows={2}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="توضیحات اضافی..."
              sx={{ mt: 2 }}
              InputLabelProps={{
                sx: { fontFamily: 'Vazir, sans-serif' },
              }}
              inputProps={{
                sx: { fontFamily: 'Vazir, sans-serif' },
              }}
            />
            {uploadingImage && (
              <Box sx={{ textAlign: 'center', mt: 2 }}>
                <CircularProgress size={24} />
                <Typography
                  variant="caption"
                  sx={{ fontFamily: 'Vazir, sans-serif', display: 'block', mt: 1 }}
                >
                  در حال آپلود...
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button
              onClick={handleCloseModal}
              sx={{ fontFamily: 'Vazir, sans-serif' }}
            >
              انصراف
            </Button>
            <Button
              type="submit"
              variant="contained"
              sx={{ fontFamily: 'Vazir, sans-serif' }}
            >
              {editingMeal ? 'به‌روزرسانی' : 'ذخیره'}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

    </Box>
  );
};

export default ManagerMeals;
